import { useSnackbar } from 'notistack';
// form
import { useFormContext, Controller } from 'react-hook-form';

import { useDebounce } from 'use-debounce';

import { useEffect, useState } from 'react';
import { getItemRecursivo } from '../../utils/DataUtil';
// utils
import axios from '../../utils/axios';
import { cn } from '../../lib/utils';
import { CLASSES_ENTRADA, ErroCampo, sxParaEstilo } from './RHFTextField';

/* ============================================================================
   Combo de busca no backend - o campo mais critico do projeto: alimenta grupo,
   usuario, aplicativo, regional, politica e os modais de dispositivo.

   O QUE **NAO** MUDOU (de proposito, verificado linha a linha):

   - Os cinco useEffect abaixo estao IDENTICOS aos de antes, inclusive as
     dependencias, a ordem entre eles, os `return` de guarda e as mensagens do
     enqueueSnackbar. Varios chamadores dependem do efeito colateral de
     `setValue(name, '')` quando `awaitCodigoPendente` esta ligado.
   - A MONTAGEM DA URL PAGINADA continua caractere por caractere a mesma:
       `${urlAPI}/paginado?page=0&rows=50&valor=${searchText}&campo=${campoPesquisaPaginado}${urlExtensao}`
     Isto e contrato com o backend e com cada tela que passa `urlExtensao` com o
     `&` na frente. Nao foi tocado, nem para acrescentar encodeURIComponent - isso
     mudaria a query enviada.
   - O debounce continua em 650ms sobre o texto digitado.
   - `field.onChange(opcao)` continua entregando o OBJETO da opcao (ou o array de
     objetos quando `multiple`), nunca o codigo solto.

   O QUE MUDOU: apenas a camada visual - o `Autocomplete`/`TextField` do MUI virou
   um input com lista, no mesmo padrao do `SeletorGrupo` de
   pages/dashboard/politicas-novo/new/[name].tsx.

   DEFEITO CORRIGIDO - o campo nao mostrava o que estava selecionado. O
   `renderInput` fazia `<TextField {...params} value={objectValue} ...>`, e esse
   `value` vinha DEPOIS de `params`, sobrescrevendo o valor que o MUI calcula a
   partir da opcao escolhida. Efeito pratico: depois de escolher "Regional Sul" o
   campo continuava exibindo o TEXTO DA BUSCA (ex.: "sul"), e ao reabrir um
   cadastro existente o campo aparecia VAZIO mesmo com o formulario preenchido -
   o usuario reescolhia o valor que ja estava lá. Agora o input mostra a busca
   enquanto esta aberto e o rotulo do selecionado quando fechado, que e o
   comportamento esperado de um combo.

   ACESSIBILIDADE: `role="combobox"` + `aria-expanded`, `aria-label` em todo botao
   de icone (limpar, remover chip) e Esc fecha a lista. As opcoes sao <button>
   FORA de qualquer <label> - dentro de label, o clique no botao dispara tambem o
   clique do label, o foco volta ao input e o `onFocus` reabre a lista logo depois
   de escolher.
   ============================================================================ */

export default function RHFAutoComplete({
    name,
    objectValueSearchAPI = '',
    // valorInicial = null || undefined || [] || {} || '' || object || new Array() || unknown || NaN,
    valorInicial = null || undefined || {} || [],
    awaitCodigoPendente = false,
    valorDependenteURL = false,
    apiExterna = false,
    isMounted = false,
    urlAPI = '',
    defaultValue = null,
    children = null,
    urlExtensao = '',
    label = '',
    codigoData = '',
    labelData = '',
    multiple = false,
    isPaginado = false,
    isLista = false,
    sx = null,
    isLoadingProps = false,
    campoPesquisaPaginado = 'codigo',
    restart = '',
    disabled = false,
    // Aceitas e descartadas: eram aparencia do MUI e nao podem cair no `...resto`
    // e virar atributo do <input> (`fullwidth="true"` no DOM gera aviso do React).
    fullWidth = undefined,
    size = undefined,
    variant = undefined,
    slotProps = undefined,
    helperText = undefined,
    autoComplete = undefined,
    // `ref` fica separado: no React 19 ele chega como prop normal em componente de
    // funcao, e se entrar no `...resto` do <input> sobrescreve o ref do
    // react-hook-form (que e o que da foco no campo reprovado na validacao).
    ref: refExterna = undefined,
    ...other }) {
    const { control, setValue, } = useFormContext();
    const [data, setData] = useState([]);
    // const currentData = useRef(null);
    // const currentSearchText = useRef(null);
    const [objectValue, setObjectValue] = useState('');
    const [isLoading, setIsLoading] = useState(isLoadingProps || false);
    const [searchText] = useDebounce(objectValue, 650);
    const { enqueueSnackbar } = useSnackbar();
    // Estado SO da camada visual: se a lista de sugestoes esta aberta.
    const [aberto, setAberto] = useState(false);
    // useEffect(() => {
    //     if (currentSearchText?.current != searchText)
    //         currentSearchText?.current = searchText;
    // }, [currentSearchText.current, searchText]);

    // useEffect(() => {
    //     if (currentData?.current != data)
    //         currentData?.current = data;
    // }, [currentData.current, data]);

    /* ------------------------------------------------------------------------
       DEFEITO CORRIGIDO (19/08/2026) - O CAMPO TRAVAVA EM "Buscando..." PARA
       SEMPRE, E NENHUMA BUSCA FUNCIONAVA DEPOIS.

       Os cinco efeitos abaixo tinham esta forma:

           try {
               const loads = async () => { ...await axios.get... }
               loads()               // <- sem await
           } catch (error) { setIsLoading(false); enqueueSnackbar(...) }

       O `try/catch` envolve a CHAMADA de `loads()`, que devolve uma Promise na
       hora. Quando o `await` la dentro falha (401, timeout, rede fora), o erro
       vira REJEICAO DE PROMISE - e rejeicao nao passa por `catch` sincrono. Ou
       seja: aquele `catch` nunca rodava. Em cadeia:

         1. `setIsLoading(false)` nunca acontecia -> `isLoading` ficava `true`;
         2. TODOS os efeitos comecam com `if (isLoading) return` -> dali em diante
            nenhuma busca era disparada de novo, em nenhum destes campos;
         3. a lista mostrava "Buscando..." indefinidamente, porque e `isLoading`
            que desenha esse texto;
         4. o erro sumia - sem mensagem para o usuario e sem nada no console
            alem de "unhandled rejection".

       Como foi encontrado: o print do usuario na tela de criar conta, lista aberta
       em "Buscando..." e nenhum resultado. Reproduzido batendo em
       `/regionais/paginado` sem token.

       Agora o `try/catch` esta DENTRO da funcao async e o `setIsLoading(false)`
       vai em `finally` - o latch abre mesmo em erro que eu nao previ aqui.

       A montagem da URL, o debounce, as dependencias e a ordem dos efeitos
       continuam identicos.
       ------------------------------------------------------------------------ */

    useEffect(() => {
        const loads = async () => {
            if (isLoading) return
            if (!apiExterna) return
            if (isMounted) return

            if (awaitCodigoPendente && !valorDependenteURL) {
                setValue(`${name}`, '')
                setData([])
                return // esperando carregar
            }

            if (!urlAPI) return

            try {
                setIsLoading(true)
                const response = await fetch(`${urlAPI}`);
                const dataResponse = await response.json();
                setData(getItemRecursivo(objectValueSearchAPI, dataResponse))
            } catch (error) {
                enqueueSnackbar(`Ops,\nOcorreu um erro ao carregar de ${name}`, { variant: 'error', style: { whiteSpace: 'pre-line' } })
            } finally {
                setIsLoading(false)
            }
        }
        loads()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlAPI, objectValueSearchAPI, awaitCodigoPendente, valorDependenteURL, apiExterna, isMounted]);

    useEffect(() => {
        const loads = async () => {
            if (isLoading) return
            if (apiExterna || isPaginado) return // consulta normal no back end sem paginado

            if (awaitCodigoPendente && !valorDependenteURL) {
                setValue(`${name}`, '')
                setData([])
                return // esperando carregar
            }

            if (!urlAPI) return

            try {
                setIsLoading(true)
                const response = await axios.get(`${urlAPI}`);
                const dataResponse = await response.data;
                setData(getItemRecursivo(objectValueSearchAPI, dataResponse))
            } catch (error) {
                enqueueSnackbar(`Ops,\nOcorreu um erro ao carregar de ${name}`, { variant: 'error', style: { whiteSpace: 'pre-line' } })
            } finally {
                setIsLoading(false)
            }
        }
        loads()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlAPI, objectValueSearchAPI, awaitCodigoPendente, valorDependenteURL, apiExterna, // isMounted,
        isPaginado]);

    /* SEGUNDA MUDANCA, so neste efeito: o `if (isLoading) return` SAIU.
       Este e o efeito da busca digitada, e a guarda descartava em silencio a
       consulta do texto novo quando a anterior ainda estava no ar - a lista
       ficava com o resultado de um texto que o usuario ja havia apagado. No lugar
       dela, um flag de cancelamento na limpeza do efeito: resposta atrasada e
       IGNORADA em vez de sobrescrever a mais recente. */
    useEffect(() => {
        let cancelado = false
        const loads = async () => {
            if (apiExterna || !isPaginado) return // consulta no banco de dados com paginado

            if (awaitCodigoPendente && !valorDependenteURL) {
                setValue(`${name}`, '')
                setData([])
                return // esperando carregar
            }

            if (!urlAPI) return

            try {
                setIsLoading(true)
                const response = await axios.get(`${urlAPI}/paginado?page=${0}&rows=${50}&valor=${searchText}&campo=${campoPesquisaPaginado}${urlExtensao ? urlExtensao : ''}`);
                const dataResponse = await response.data.content;
                if (cancelado) return
                setData(getItemRecursivo(objectValueSearchAPI, dataResponse))
            } catch (error) {
                if (cancelado) return
                setData([])
                enqueueSnackbar(`Ops,\nOcorreu um erro ao carregar de ${name}`, { variant: 'error', style: { whiteSpace: 'pre-line' } })
            } finally {
                if (!cancelado) setIsLoading(false)
            }
        }
        loads()
        return () => { cancelado = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlAPI, objectValueSearchAPI, awaitCodigoPendente, valorDependenteURL, apiExterna, isMounted, searchText, urlExtensao, restart, isPaginado]);

    useEffect(() => {
        try {
            if (isLoading) return
            if (!!data && data?.length > 0 && valorInicial && !isLista) {
                const valorEncontrado = data.find(item => getItemRecursivo(`${codigoData}`, item) == valorInicial);
                if (valorEncontrado) setValue(`${name}`, valorEncontrado)
            } else if (!!data && data?.length > 0 && !!valorInicial && isLista) {
                if (!!valorInicial && valorInicial?.length > 0) setValue(`${name}`, valorInicial)
            }
        } catch (error) {
            enqueueSnackbar(`Ops,\nOcorreu um erro ao carregar de ${name}`, { variant: 'error', style: { whiteSpace: 'pre-line' } })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valorInicial, codigoData, name, setValue, enqueueSnackbar, data, isLista])

    useEffect(() => { // limpar o campo se tiver dependente
        try {
            if (isLoading) return
            if (valorDependenteURL) {
                setValue(`${name}`, '')
                setData([])
            }
        } catch (error) {
            enqueueSnackbar(`Ops,\nOcorreu um erro ao carregar de ${name}`, { variant: 'error', style: { whiteSpace: 'pre-line' } })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valorDependenteURL, enqueueSnackbar, setValue, name])

    // ------------------------------------------------------------------------
    // Camada visual
    // ------------------------------------------------------------------------

    const idCampo = other.id || `campo-${name}`;
    const idErro = `${idCampo}-erro`;
    const rotuloDe = (opcao) => (opcao && opcao[labelData] ? opcao[labelData] : '');

    // Mesma regra do `isOptionEqualToValue` que estava no Autocomplete: compara
    // pelo campo de codigo, com == para tolerar codigo numerico chegando como
    // string do backend.
    const mesmaOpcao = (a, b) => {
        if (!a || !b || !a[codigoData] || !b[codigoData]) return false;
        /* eslint-disable-next-line eqeqeq */
        return a[codigoData] == b[codigoData];
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                // No modo `multiple` o valor e um array; fora dele, um objeto. O
                // campo pode chegar como '' (os efeitos acima fazem setValue(name, '')),
                // por isso as duas leituras sao defensivas.
                const selecionados = multiple ? (Array.isArray(field.value) ? field.value : []) : [];
                const selecionado = !multiple && field.value && typeof field.value === 'object' ? field.value : null;

                const escolher = (opcao) => {
                    if (multiple) {
                        const jaTem = selecionados.some((s) => mesmaOpcao(s, opcao));
                        // Alterna, como o Autocomplete do MUI fazia com
                        // `disableCloseOnSelect` desligado: clicar de novo remove.
                        field.onChange(jaTem ? selecionados.filter((s) => !mesmaOpcao(s, opcao)) : [...selecionados, opcao]);
                        setObjectValue('');
                        setAberto(false);
                        return;
                    }
                    field.onChange(opcao);
                    setAberto(false);
                };

                // Campo travado: mostra o que esta escolhido em caixa de leitura.
                // Sem input, para nao dar a impressao de que da para digitar.
                if (disabled) {
                    const textoTravado = multiple
                        ? selecionados.map(rotuloDe).filter(Boolean).join(', ')
                        : rotuloDe(selecionado);

                    return (
                        <div ref={refExterna} className="w-full" style={sxParaEstilo(sx)}>
                            {label && <span className="mb-1 block text-[12px] font-semibold text-ink">{label}</span>}
                            {/* min-h-[36px] em valor arbitrario, e nao `min-h-9`: a
                                escala de espacamento de min-height so entrou no
                                Tailwind 3.4 e este projeto esta no 3.3.2 - a classe
                                `min-h-9` simplesmente nao geraria CSS, sem erro
                                nenhum, e a caixa colapsaria quando estivesse vazia. */}
                            <div className="flex min-h-[36px] items-center rounded-lg border border-line bg-soft px-3 text-[12.5px] text-muted-foreground">
                                {textoTravado || '—'}
                            </div>
                            <ErroCampo id={idErro}>{error?.message}</ErroCampo>
                        </div>
                    );
                }

                return (
                    // Sem <label> envolvendo tudo: as opcoes sao botoes, e botao
                    // dentro de label dispara o clique do label junto.
                    <div ref={refExterna} className="w-full" style={sxParaEstilo(sx)}>
                        {label && (
                            <label htmlFor={idCampo} className="mb-1 block text-[12px] font-semibold text-ink">
                                {label}
                            </label>
                        )}

                        {multiple && selecionados.length > 0 && (
                            <div className="mb-1 flex flex-wrap gap-1">
                                {selecionados.map((item, indice) => (
                                    <span
                                        key={(codigoData && item?.[codigoData]) ?? `${rotuloDe(item)}-${indice}`}
                                        className="inline-flex items-center gap-1 rounded-md border border-line bg-soft px-2 py-0.5 text-[12px] text-ink"
                                    >
                                        {rotuloDe(item)}
                                        <button
                                            type="button"
                                            aria-label={`Remover ${rotuloDe(item)}`}
                                            onClick={() => field.onChange(selecionados.filter((s) => !mesmaOpcao(s, item)))}
                                            className="text-faint hover:text-danger"
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="relative">
                            <input
                                {...other}
                                id={idCampo}
                                name={field.name}
                                ref={field.ref}
                                role="combobox"
                                aria-expanded={aberto}
                                aria-autocomplete="list"
                                aria-invalid={Boolean(error)}
                                aria-describedby={error ? idErro : undefined}
                                placeholder={multiple ? 'Digite para buscar e adicionar' : 'Digite para buscar'}
                                // Aberto: mostra o que se esta digitando. Fechado:
                                // mostra o escolhido. No modo multiple o escolhido
                                // esta nos chips acima, entao o input fica vazio.
                                value={aberto ? objectValue : multiple ? '' : rotuloDe(selecionado)}
                                onFocus={() => {
                                    setAberto(true);
                                    setObjectValue('');
                                }}
                                onBlur={(e) => {
                                    field.onBlur(e);
                                    // Atraso para o onMouseDown da opcao acontecer
                                    // antes de a lista ser desmontada.
                                    setTimeout(() => setAberto(false), 150);
                                }}
                                // ESTA e a linha que alimenta o debounce -> searchText
                                // -> URL paginada. Nao mexer.
                                onChange={(e) => setObjectValue(e.target.value)}
                                onKeyDown={(e) => e.key === 'Escape' && setAberto(false)}
                                className={cn(
                                    CLASSES_ENTRADA,
                                    'pr-16',
                                    error && 'border-danger'
                                )}
                            />

                            {/* Indicador de carregando dentro do campo. Antes era um
                                CircularProgress ACIMA do campo, posicionado com
                                top/left em rem, que empurrava o layout da linha. */}
                            {isLoading && (
                                <span
                                    className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 text-[11px] text-faint"
                                    role="status"
                                >
                                    ...
                                </span>
                            )}

                            {!multiple && selecionado && (
                                <button
                                    type="button"
                                    aria-label="Limpar seleção"
                                    // null (e nao '') para manter o que o Autocomplete
                                    // entregava ao limpar - as validacoes Yup das telas
                                    // sao escritas contra esse valor.
                                    onClick={() => {
                                        field.onChange(null);
                                        setObjectValue('');
                                    }}
                                    className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[12px] text-faint hover:bg-soft hover:text-ink"
                                >
                                    ✕
                                </button>
                            )}

                            {aberto && (
                                <div
                                    role="listbox"
                                    className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-card"
                                >
                                    {isLoading && <div className="px-3 py-2 text-[12px] text-faint">Buscando...</div>}
                                    {/* Vazio SEM busca digitada nao e "nenhum resultado" - e
                                        "ainda nao pesquisei". Dizer "Nenhum resultado" numa
                                        lista que o usuario nem consultou faz parecer que a
                                        base esta vazia. */}
                                    {!isLoading && (!data || data.length === 0) && (
                                        <div className="px-3 py-2 text-[12px] text-faint">
                                            {objectValue.trim().length === 0
                                                ? 'Digite para buscar.'
                                                : objectValue.trim().length < 2
                                                    ? 'Digite ao menos 2 letras.'
                                                    : 'Nenhum resultado.'}
                                        </div>
                                    )}
                                    {(data || []).map((opcao, indice) => {
                                        const marcada = multiple
                                            ? selecionados.some((s) => mesmaOpcao(s, opcao))
                                            : mesmaOpcao(selecionado, opcao);

                                        return (
                                            <button
                                                // Chave pelo CODIGO, nao pelo rotulo: a base
                                                // tem nomes repetidos (dois aplicativos
                                                // "BEES", dois "APR DIGITAL") e chave
                                                // duplicada faz o React omitir ou duplicar
                                                // opcao - o usuario ve a lista errada.
                                                key={(codigoData && opcao?.[codigoData]) ?? `${rotuloDe(opcao)}-${indice}`}
                                                type="button"
                                                role="option"
                                                aria-selected={marcada}
                                                onMouseDown={() => escolher(opcao)}
                                                className={cn(
                                                    'block w-full px-3 py-2 text-left text-[12px] hover:bg-soft',
                                                    marcada && 'bg-soft font-semibold'
                                                )}
                                            >
                                                {rotuloDe(opcao) || '—'}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <ErroCampo id={idErro}>{error?.message}</ErroCampo>
                    </div>
                );
            }}
        />
    );
}
